import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Landmark, Mail, Send, Database } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";

const EMPTY = { name: "", price_monthly: "", price_annual: "", max_staff: 2, max_items: 200, features: "", active: true, whatsapp_auto: false, whatsapp_monthly_quota: 0, whatsapp_overage_price: 0 };
const EMPTY_BANK = { bank_name: "", account_name: "", account_number: "", iban: "", swift: "", note: "", active: true };

export default function AdminPlans() {
  const { t } = useLang();
  const [plans, setPlans] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [banks, setBanks] = useState([]);
  const [bankOpen, setBankOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [bankForm, setBankForm] = useState(EMPTY_BANK);
  const [emailTo, setEmailTo] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [storageBusy, setStorageBusy] = useState(false);
  const [storageResult, setStorageResult] = useState(null);

  const runStorageCheck = async () => {
    setStorageBusy(true);
    setStorageResult(null);
    try {
      const { data } = await API.post("/admin/test-storage");
      setStorageResult(data);
      if (data.ok) toast.success(t("admin.storage_test_ok"));
      else toast.error(t("admin.storage_test_fail"));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setStorageBusy(false);
    }
  };

  const sendTestEmail = async (e) => {
    e.preventDefault();
    setEmailBusy(true);
    setEmailResult(null);
    try {
      const { data } = await API.post("/admin/test-email", { to: emailTo });
      setEmailResult(data);
      if (data.sent) toast.success(t("admin.email_test_ok"));
      else toast.error(t("admin.email_test_fail"));
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setEmailBusy(false);
    }
  };

  const load = () => API.get("/admin/plans").then((r) => setPlans(r.data)).catch(() => setPlans([]));
  const loadBanks = () => API.get("/admin/bank-accounts").then((r) => setBanks(r.data)).catch(() => setBanks([]));
  useEffect(() => { load(); loadBanks(); }, []);

  const openBankEdit = (b) => {
    setEditingBank(b);
    setBankForm({ bank_name: b.bank_name, account_name: b.account_name || "", account_number: b.account_number || "", iban: b.iban || "", swift: b.swift || "", note: b.note || "", active: b.active !== false });
    setBankOpen(true);
  };

  const submitBank = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editingBank) await API.patch(`/admin/bank-accounts/${editingBank.id}`, bankForm);
      else await API.post("/admin/bank-accounts", bankForm);
      toast.success(t("msg.saved"));
      setBankOpen(false);
      loadBanks();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const removeBank = async (b) => {
    if (!window.confirm(`${t("common.delete")} ${b.bank_name}?`)) return;
    try {
      await API.delete(`/admin/bank-accounts/${b.id}`);
      toast.success(t("msg.deleted"));
      loadBanks();
    } catch (err) {
      toast.error(fmtErr(err));
    }
  };

  if (!plans) return <p className="text-slate-500">{t("common.loading")}</p>;

  const openEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, price_monthly: p.price_monthly, price_annual: p.price_annual, max_staff: p.max_staff, max_items: p.max_items, features: (p.features || []).join("\n"), active: p.active, whatsapp_auto: !!p.whatsapp_auto, whatsapp_monthly_quota: p.whatsapp_monthly_quota || 0, whatsapp_overage_price: p.whatsapp_overage_price || 0 });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: form.name,
      price_monthly: Number(form.price_monthly),
      price_annual: Number(form.price_annual),
      max_staff: Number(form.max_staff),
      max_items: Number(form.max_items),
      whatsapp_monthly_quota: Number(form.whatsapp_monthly_quota) || 0,
      whatsapp_overage_price: Number(form.whatsapp_overage_price) || 0,
      features: form.features.split("\n").map((f) => f.trim()).filter(Boolean),
      active: form.active,
    };
    try {
      if (editing) await API.patch(`/admin/plans/${editing.id}`, payload);
      else await API.post("/admin/plans", payload);
      toast.success(t("msg.saved"));
      setOpen(false);
      load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-plans-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("nav.plans")}</h1>
        <Button data-testid="new-plan-button" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}
          className="bg-amber-600 hover:bg-amber-700 text-white">
          <Plus className="h-4 w-4 mr-1.5" />{t("admin.new_plan")}
        </Button>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => (
          <div key={p.id} className={`bg-white rounded-xl border p-6 ${p.active ? "border-slate-200" : "border-slate-200 opacity-60"}`} data-testid={`admin-plan-card-${p.key}`}>
            <div className="flex items-start justify-between">
              <h3 className="font-heading text-lg font-semibold">{p.name}</h3>
              <button data-testid={`edit-plan-${p.key}`} onClick={() => openEdit(p)} className="p-2 hover:bg-slate-100 rounded-lg">
                <Pencil className="h-4 w-4 text-slate-600" />
              </button>
            </div>
            <div className="mt-3 font-heading text-2xl font-bold text-slate-900">{money(p.price_monthly)}<span className="text-sm font-normal text-slate-500">{t("landing.per_month")}</span></div>
            <div className="text-sm text-slate-500">{money(p.price_annual)}{t("landing.per_year")}</div>
            <ul className="mt-4 space-y-1.5 text-sm text-slate-600">
              {(p.features || []).map((f, i) => <li key={i}>· {f}</li>)}
            </ul>
            <div className="mt-4 text-xs font-mono uppercase tracking-wider">
              <span className={p.active ? "text-emerald-600" : "text-slate-400"}>{p.active ? t("status.active") : "inactive"}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-10" data-testid="bank-accounts-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Landmark className="h-5 w-5 text-amber-600" />{t("admin.bank_accounts")}
          </h2>
          <Button variant="outline" size="sm" data-testid="add-bank-account-button"
            onClick={() => { setEditingBank(null); setBankForm(EMPTY_BANK); setBankOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" />{t("admin.add_bank")}
          </Button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 divide-y">
          {banks.length === 0 && <p className="text-sm text-slate-500 p-4">{t("common.none")}</p>}
          {banks.map((b) => (
            <div key={b.id} className="flex items-center justify-between p-4 gap-3" data-testid={`bank-account-row-${b.id.slice(0, 8)}`}>
              <div className="min-w-0">
                <div className="font-medium text-slate-900 flex items-center gap-2">
                  {b.bank_name}
                  {!b.active && <span className="text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">inactive</span>}
                </div>
                <div className="text-xs text-slate-500 font-mono truncate">
                  {b.account_name} · {b.account_number}{b.iban ? ` · IBAN ${b.iban}` : ""}{b.swift ? ` · ${b.swift}` : ""}
                </div>
              </div>
              <div className="flex shrink-0">
                <button data-testid={`edit-bank-${b.id.slice(0, 8)}`} onClick={() => openBankEdit(b)} className="p-2 hover:bg-slate-100 rounded-lg">
                  <Pencil className="h-4 w-4 text-slate-600" />
                </button>
                <button data-testid={`delete-bank-${b.id.slice(0, 8)}`} onClick={() => removeBank(b)} className="p-2 hover:bg-red-50 rounded-lg">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-10" data-testid="email-test-section">
        <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Mail className="h-5 w-5 text-amber-600" />{t("admin.email_test")}
        </h2>
        <form onSubmit={sendTestEmail} className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs">{t("admin.email_test_to")}</Label>
            <Input data-testid="test-email-input" type="email" required value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)} placeholder="you@example.com" className="mt-1" />
          </div>
          <Button type="submit" disabled={emailBusy} data-testid="send-test-email-button">
            <Send className="h-4 w-4 mr-1" />{emailBusy ? t("common.loading") : t("admin.email_test_send")}
          </Button>
        </form>
        {emailResult && (
          <p className={`text-sm mt-2 ${emailResult.sent ? "text-emerald-700" : "text-red-600"}`} data-testid="test-email-result">
            {emailResult.sent ? t("admin.email_test_ok") : t("admin.email_test_fail")} · {t("admin.email_test_provider")}: {emailResult.provider}
          </p>
        )}
      </div>

      <div className="mt-10" data-testid="storage-test-section">
        <h2 className="font-heading text-xl font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Database className="h-5 w-5 text-amber-600" />{t("admin.storage_test")}
        </h2>
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <p className="text-sm text-slate-600">{t("admin.storage_test_desc")}</p>
          <Button variant="outline" onClick={runStorageCheck} disabled={storageBusy} data-testid="run-storage-test-button" className="shrink-0">
            <Database className="h-4 w-4 mr-1" />{storageBusy ? t("common.loading") : t("admin.storage_test_run")}
          </Button>
        </div>
        {storageResult && (
          <p className={`text-sm mt-2 ${storageResult.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="storage-test-result">
            {storageResult.ok ? t("admin.storage_test_ok") : t("admin.storage_test_fail")} · {t("admin.storage_backend")}: {storageResult.backend}{storageResult.error ? ` — ${storageResult.error}` : ""}
          </p>
        )}
      </div>

      <Dialog open={bankOpen} onOpenChange={setBankOpen}>
        <DialogContent className="bg-white" data-testid="bank-account-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editingBank ? t("common.edit") : t("admin.add_bank")}</DialogTitle></DialogHeader>
          <form onSubmit={submitBank} className="space-y-4">
            <div>
              <Label>{t("bank.name")}</Label>
              <Input data-testid="bank-name-input" required minLength={2} value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("bank.acc_name")}</Label>
                <Input data-testid="bank-acc-name-input" value={bankForm.account_name}
                  onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("bank.acc_no")}</Label>
                <Input data-testid="bank-acc-no-input" value={bankForm.account_number}
                  onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("bank.iban")}</Label>
                <Input data-testid="bank-iban-input" value={bankForm.iban}
                  onChange={(e) => setBankForm({ ...bankForm, iban: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("bank.swift")}</Label>
                <Input data-testid="bank-swift-input" value={bankForm.swift}
                  onChange={(e) => setBankForm({ ...bankForm, swift: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t("fin.note")}</Label>
              <Input data-testid="bank-note-input" value={bankForm.note}
                onChange={(e) => setBankForm({ ...bankForm, note: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="bank-active-switch" checked={bankForm.active} onCheckedChange={(v) => setBankForm({ ...bankForm, active: v })} />
              <Label>{t("status.active")}</Label>
            </div>
            <Button data-testid="bank-save-button" type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="plan-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("admin.edit_plan") : t("admin.new_plan")}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("common.name")}</Label>
              <Input data-testid="plan-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("admin.price_monthly")}</Label>
                <Input data-testid="plan-price-monthly-input" type="number" min="0" step="0.01" required value={form.price_monthly}
                  onChange={(e) => setForm({ ...form, price_monthly: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("admin.price_annual")}</Label>
                <Input data-testid="plan-price-annual-input" type="number" min="0" step="0.01" required value={form.price_annual}
                  onChange={(e) => setForm({ ...form, price_annual: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("admin.max_staff")}</Label>
                <Input data-testid="plan-max-staff-input" type="number" required value={form.max_staff}
                  onChange={(e) => setForm({ ...form, max_staff: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label>{t("admin.max_items")}</Label>
                <Input data-testid="plan-max-items-input" type="number" required value={form.max_items}
                  onChange={(e) => setForm({ ...form, max_items: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>{t("admin.features")}</Label>
              <textarea data-testid="plan-features-input" rows={4} value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm" />
            </div>
            <div className="border border-green-200 bg-green-50 rounded-lg p-3 space-y-2" data-testid="plan-whatsapp-gate">
              <div className="flex items-center gap-2">
                <Switch data-testid="plan-whatsapp-auto-switch" checked={form.whatsapp_auto} onCheckedChange={(v) => setForm({ ...form, whatsapp_auto: v })} />
                <Label>{t("admin.wa_auto")}</Label>
              </div>
              {form.whatsapp_auto && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">{t("admin.wa_quota")}</Label>
                    <Input data-testid="plan-whatsapp-quota-input" type="number" min="0" value={form.whatsapp_monthly_quota}
                      onChange={(e) => setForm({ ...form, whatsapp_monthly_quota: e.target.value })} className="mt-1 h-9" />
                  </div>
                  <div>
                    <Label className="text-xs">{t("admin.wa_overage")}</Label>
                    <Input data-testid="plan-whatsapp-overage-input" type="number" min="0" step="0.01" value={form.whatsapp_overage_price}
                      onChange={(e) => setForm({ ...form, whatsapp_overage_price: e.target.value })} className="mt-1 h-9" />
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-500">{t("admin.wa_hint")}</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch data-testid="plan-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>{t("status.active")}</Label>
            </div>
            <Button data-testid="plan-save-button" type="submit" disabled={busy} className="w-full bg-amber-600 hover:bg-amber-700 text-white">{t("common.save")}</Button>
          </form>
        </DialogContent>
      </Dialog>
      <WhatsAppUsage />
    </div>
  );
}

function WhatsAppUsage() {
  const { t } = useLang();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  useEffect(() => { API.get("/admin/whatsapp-usage", { params: { month } }).then((r) => setData(r.data)).catch(() => {}); }, [month]);
  if (!data) return null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mt-8" data-testid="admin-whatsapp-usage">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <h2 className="font-heading text-lg font-medium">{t("admin.wa_usage")}</h2>
        <Input type="month" data-testid="wa-usage-month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44" />
        <span className="ml-auto text-sm text-slate-600">{t("admin.wa_total")}: <b className="font-mono">{data.total_messages}</b> · {t("admin.wa_overage_total")}: <b className="font-mono">{money(data.total_overage_charge)}</b></span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-1.5">{t("admin.shop")}</th><th>{t("nav.plans")}</th><th className="text-right">{t("admin.wa_used")}</th><th className="text-right">{t("admin.wa_quota")}</th><th className="text-right">{t("admin.wa_overage_count")}</th><th className="text-right">{t("admin.wa_charge")}</th></tr></thead>
        <tbody>
          {data.rows.map((r) => (
            <tr key={r.shop_id} className="border-b border-slate-100" data-testid={`wa-usage-row-${r.slug}`}>
              <td className="py-1.5">{r.shop_name}</td>
              <td className="text-xs">{r.plan_name || "—"} {r.whatsapp_auto ? <span className="text-green-700">· auto</span> : <span className="text-slate-400">· click-to-chat</span>}</td>
              <td className="text-right font-mono">{r.used}</td><td className="text-right font-mono">{r.quota || "∞"}</td>
              <td className="text-right font-mono">{r.overage}</td><td className="text-right font-mono">{money(r.overage_charge)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
